import type { MessageCenterEmailFilterOption } from '@/views/screens/MessageCenter/components/ComposeMessage/FilterComponents/email-filter-card/email-filter-card.constants';
import type { EmailContact, Message, MessageDetails, MessageFolder } from '@/views/screens/MessageCenter/types';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import MessageCenterTypes from './MessageCenterTypes';

export interface MessageCenterState extends Record<string, any> {
  activeFolderId: MessageFolder['folderID'];
  activeMessageId: Message['msgID'];
  folders: DefaultStoreObject<MessageFolder[]> & {
    unreadMessages: number;
  };
  messageCenterVisible: boolean;
  messageDetails: DefaultStoreObject<MessageDetails>;
  messageList: DefaultStoreObject<Message[]> & {
    pageIndex: number;
    pageSize: number;
    totalRecords: number;
  };
  composeEmails: DefaultStoreObject<EmailContact[]> & {
    PageIndex: number;
    PageSize: number;
    totalRecords: number;
    StartingSearchValue?: MessageCenterEmailFilterOption;
    ContainSearchValue?: MessageCenterEmailFilterOption;
  };
  deleteMessage: DefaultStoreObject<Message['msgID'][]>;
  markAsRead: DefaultStoreObject<unknown>;
  sendMessage: DefaultStoreObject<unknown>;
}

const initialState: MessageCenterState = {
  messageCenterVisible: false,
  activeFolderId: 0,
  activeMessageId: 0,
  folders: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    unreadMessages: 0,
  },
  messageList: {
    ...DEFAULT_STORE_OBJECT,
    pageIndex: 1,
    pageSize: 10,
    data: [],
    totalRecords: 0,
  },
  messageDetails: {
    ...DEFAULT_STORE_OBJECT,
    data: null,
  },
  deleteMessage: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  markAsRead: {
    ...DEFAULT_STORE_OBJECT,
  },
  composeEmails: {
    ...DEFAULT_STORE_OBJECT,
    PageIndex: 1,
    PageSize: 100,
    totalRecords: 0,
    StartingSearchValue: undefined,
    ContainSearchValue: undefined,
    data: [],
  },
  sendMessage: {
    ...DEFAULT_STORE_OBJECT,
  },
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const MessageCenterReducer = (state = initialState, { type, payload }: any): MessageCenterState => {
  switch (type) {
    case MessageCenterTypes.SET_OPEN: {
      return {
        ...state,
        messageCenterVisible: payload,
      };
    }
    case MessageCenterTypes.SET_ACTIVE_FOLDER_ID: {
      return {
        ...state,
        activeFolderId: payload,
        messageDetails: {
          ...DEFAULT_STORE_OBJECT,
          data: null,
        },
        activeMessageId: 0,
      };
    }
    case MessageCenterTypes.SET_ACTIVE_MESSAGE_ID: {
      return {
        ...state,
        activeMessageId: payload,
      };
    }

    case MessageCenterTypes.GET_FOLDERS_PROGRESS: {
      return {
        ...state,
        folders: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
          unreadMessages: 0,
        },
      };
    }
    case MessageCenterTypes.GET_FOLDERS_SUCCESS: {
      return {
        ...state,
        folders: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
          unreadMessages: payload.unreadMessages,
        },
      };
    }
    case MessageCenterTypes.GET_FOLDERS_ERROR: {
      return {
        ...state,
        folders: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          unreadMessages: 0,
          ...payload,
        },
      };
    }

    case MessageCenterTypes.GET_MESSAGE_LIST_PROGRESS: {
      return {
        ...state,
        messageList: {
          ...DEFAULT_STORE_OBJECT,
          pageIndex: payload.pageIndex,
          pageSize: payload.pageSize,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.messageList.data,
          totalRecords: state.messageList.totalRecords,
        },
      };
    }
    case MessageCenterTypes.GET_MESSAGE_LIST_SUCCESS: {
      return {
        ...state,
        messageList: {
          ...state.messageList,
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: [...(state.messageList.data || []), ...payload.data],
          totalRecords: payload.totalRecords,
        },
      };
    }
    case MessageCenterTypes.GET_MESSAGE_LIST_ERROR: {
      return {
        ...state,
        messageList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.messageList,
          progressing: false,
          error: true,
          ...payload,
        },
      };
    }

    case MessageCenterTypes.GET_MESSAGE_DETAILS_PROGRESS: {
      return {
        ...state,
        messageDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: null,
        },
      };
    }
    case MessageCenterTypes.GET_MESSAGE_DETAILS_SUCCESS: {
      return {
        ...state,
        messageDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }
    case MessageCenterTypes.GET_MESSAGE_DETAILS_ERROR: {
      return {
        ...state,
        messageDetails: {
          ...DEFAULT_STORE_OBJECT,
          data: false,
          ...payload,
        },
      };
    }

    case MessageCenterTypes.DELETE_MESSAGE_PROGRESS: {
      return {
        ...state,
        deleteMessage: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [...(state.deleteMessage.data || []), payload.msgID],
        },
      };
    }
    case MessageCenterTypes.DELETE_MESSAGE_SUCCESS: {
      return {
        ...state,
        deleteMessage: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: state.deleteMessage?.data?.filter((v) => v === payload.msgID),
        },
        messageList: { ...state.messageList, data: payload.messageListData },
      };
    }
    case MessageCenterTypes.DELETE_MESSAGE_ERROR: {
      return {
        ...state,
        deleteMessage: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.deleteMessage?.data?.filter((v) => v === payload.msgID),
        },
      };
    }

    case MessageCenterTypes.MARK_AS_READ_MESSAGE_PROGRESS: {
      return {
        ...state,
        markAsRead: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case MessageCenterTypes.MARK_AS_READ_MESSAGE_SUCCESS: {
      return {
        ...state,
        markAsRead: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
        folders: { ...state.folders, data: payload.foldersData },
        messageList: { ...state.messageList, data: payload.messageListData },
      };
    }
    case MessageCenterTypes.MARK_AS_READ_MESSAGE_ERROR: {
      return {
        ...state,
        markAsRead: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
        },
      };
    }

    case MessageCenterTypes.GET_COMPOSE_EMAILS_ID_SEARCH_PROGRESS: {
      return {
        ...state,
        composeEmails: {
          ...DEFAULT_STORE_OBJECT,
          ...payload,
          progressing: true,
          data: payload.PageIndex === 1 ? [] : state.composeEmails.data,
        },
      };
    }
    case MessageCenterTypes.GET_COMPOSE_EMAILS_ID_SEARCH_SUCCESS: {
      return {
        ...state,
        composeEmails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.composeEmails,
          progressing: false,
          success: true,
          totalRecords: payload.totalRecords,
          data: [...(state.composeEmails.data || []), ...payload.data],
        },
      };
    }
    case MessageCenterTypes.GET_COMPOSE_EMAILS_ID_SEARCH_ERROR: {
      return {
        ...state,
        composeEmails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.composeEmails,
          error: true,
          data: [...(state.composeEmails.data || [])],
        },
      };
    }

    case MessageCenterTypes.SEND_MESSAGE_PROGRESS: {
      return {
        ...state,
        sendMessage: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case MessageCenterTypes.SEND_MESSAGE_SUCCESS: {
      return {
        ...state,
        sendMessage: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }
    case MessageCenterTypes.SEND_MESSAGE_ERROR: {
      return {
        ...state,
        sendMessage: {
          ...DEFAULT_STORE_OBJECT,
          ...payload,
        },
      };
    }

    default:
      return state;
  }
};
export default MessageCenterReducer;

export const selectActiveFolderDetails = ({ messageCenter }: { messageCenter: MessageCenterState }) =>
  messageCenter?.folders?.data?.find((folder) => folder.folderID === messageCenter?.activeFolderId);

export const selectActiveMessageDetails = ({ messageCenter }: { messageCenter: MessageCenterState }) =>
  messageCenter?.messageList?.data?.find((message) => message.msgID === messageCenter?.activeMessageId);
